'use client';

import { Flex, Heading, Image, Separator, Text } from '@chakra-ui/react';
import CreateOrgButton from '@features/onboarding/CreateOrgButton';
import OrgList from '@features/onboarding/OrgList';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import pluralize from 'pluralize';
import { useCallback } from 'react';
import { IUserOrg } from 'stores/orgStore';
import { useOnboardingStore, useOrgStore } from 'stores/rootStore';

interface Props {
  getNextStepPath?: () => string | undefined;
}

const AddBusinessPage = ({ getNextStepPath }: Props) => {
  const orgStore = useOrgStore();
  const onboardingStore = useOnboardingStore();
  const router = useRouter();

  const navigateToNextStep = useCallback(
    (org: IUserOrg) => {
      onboardingStore.setSelectedOrgId(org.id);
      const nextStepPath = getNextStepPath?.();
      if (nextStepPath != null) {
        router.push(nextStepPath);
      }
    },
    [onboardingStore, router, getNextStepPath],
  );

  const onOrgCreated = navigateToNextStep;
  const onSelectBusiness = navigateToNextStep;

  return (
    <Flex flex={1} minH={0} justifyContent="center">
      <Flex
        flexDirection={{ base: 'column', lg: 'row' }}
        alignItems="center"
        justifyContent="center"
        gap={8}
        px={{ base: 4, md: 8, lg: 12 }}
        py={{ base: 8, md: 12 }}
        maxW="1400px"
      >
        <Flex
          flex={1}
          flexDirection="column"
          gap={6}
          maxW={{ lg: '480px' }}
          order={{ base: 1, lg: 0 }}
        >
          <Heading
            as="h1"
            fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
            fontWeight="bold"
            lineHeight="1.1"
            color="gray.900"
            _dark={{ color: 'white' }}
            flexShrink={0}
          >
            Time is money
          </Heading>
          <Flex flexDirection="column" gap={4}>
            <Text whiteSpace="wrap">
              Let's get you set up quickly so you can start your journey to transform your business!
            </Text>
            <CreateOrgButton
              onOrgCreated={onOrgCreated}
              title="Add new business"
              placeholder="Type name of business"
              createMenuLabel="Add business"
            />
            {orgStore.orgs.length > 0 && (
              <>
                <Flex w="full" gap={2} alignItems="center">
                  <Text fontSize="sm" flexShrink={0}>
                    Or use
                  </Text>
                  <Separator w="full" orientation="horizontal" borderColor="border.light" />
                </Flex>
                <Text fontWeight="bold" fontSize="xs">
                  {`Existing ${pluralize('business', orgStore.orgs.length)}`}
                </Text>
                <OrgList onSelectOrg={onSelectBusiness} />
              </>
            )}
          </Flex>
        </Flex>
        <Flex
          w={{ base: '100%', lg: '50%' }}
          flexShrink={0}
          alignItems="center"
          justifyContent="center"
          minH={{ base: '280px', md: '360px' }}
          order={{ base: 0, lg: 1 }}
        >
          <Image
            src="/images/time-is-money.png"
            alt="Time is money"
            htmlWidth={1536}
            htmlHeight={1024}
            maxH={{ base: '280px', md: '400px', lg: '500px' }}
            objectFit="contain"
            style={{ width: '100%', aspectRatio: '1536 / 1024' }}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default observer(AddBusinessPage);
